import React, { useState, useEffect } from 'react';
import { format, startOfWeek, endOfWeek, startOfMonth, endOfMonth } from 'date-fns';
import { Download, Calendar, Filter, FileText, Users, User } from 'lucide-react';
import { Squad, AttendanceReportSubject } from '../types';
import { getSessionsForAttendanceReport, getCoachAttendanceStats, getPlayerAttendanceStats } from '../services/reportService';
import { generateCoachAttendanceReport, generatePlayerAttendanceReport } from './AttendanceReportPDF';
import { getCoaches } from '../services/coachService';
import toast from 'react-hot-toast';

interface AttendanceReportModalProps {
  onClose: () => void;
}

type DateRange = 'week' | 'month' | 'custom';

const AttendanceReportModal: React.FC<AttendanceReportModalProps> = ({ onClose }) => {
  const [dateRange, setDateRange] = useState<DateRange>('week');
  const [startDate, setStartDate] = useState<string>(format(startOfWeek(new Date()), 'yyyy-MM-dd'));
  const [endDate, setEndDate] = useState<string>(format(endOfWeek(new Date()), 'yyyy-MM-dd'));
  const [reportSubject, setReportSubject] = useState<AttendanceReportSubject>('player');
  const [selectedCoach, setSelectedCoach] = useState<string>('');
  const [selectedSquads, setSelectedSquads] = useState<Squad[]>(['U11', 'U13', 'U15', 'U17']);
  const [separateReports, setSeparateReports] = useState<boolean>(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [coaches, setCoaches] = useState<any[]>([]);

  useEffect(() => {
    const loadCoaches = async () => {
      try {
        const data = await getCoaches();
        setCoaches(data);
      } catch (error) {
        console.error('Error loading coaches:', error);
        toast.error('Failed to load coaches');
      }
    };
    loadCoaches();
  }, []);

  const handleDateRangeChange = (range: DateRange) => {
    setDateRange(range);
    
    const today = new Date();
    
    if (range === 'week') {
      setStartDate(format(startOfWeek(today), 'yyyy-MM-dd'));
      setEndDate(format(endOfWeek(today), 'yyyy-MM-dd'));
    } else if (range === 'month') {
      setStartDate(format(startOfMonth(today), 'yyyy-MM-dd'));
      setEndDate(format(endOfMonth(today), 'yyyy-MM-dd'));
    }
  };

  const handleSquadChange = (squad: Squad, checked: boolean) => {
    if (checked) {
      setSelectedSquads(prev => [...prev, squad]);
    } else {
      setSelectedSquads(prev => prev.filter(s => s !== squad));
    }
  };

  const handleGeneratePDF = async () => {
    try {
      setIsGenerating(true);

      if (reportSubject === 'coach') {
        // Get coach attendance data
        const { sessions, coachStats } = await getCoachAttendanceStats(startDate, endDate, selectedCoach);

        if (!sessions || sessions.length === 0) {
          toast.error('No sessions found with marked attendance in the selected date range');
          return;
        }

        // Generate coach attendance report
        generateCoachAttendanceReport({
          sessions,
          coachStats,
          startDate,
          endDate,
          coachId: selectedCoach
        });

        toast.success('Coach attendance report generated successfully');
        onClose();
      } else {
        // Get sessions for player attendance
        const sessions = await getSessionsForAttendanceReport(
          startDate,
          endDate,
          selectedSquads
        );

        if (!sessions || sessions.length === 0) {
          toast.error('No sessions found with marked attendance in the selected date range');
          return;
        }

        // Generate player attendance report
        const playerStats = await Promise.all(
          selectedSquads.map(async squad => {
            const stats = await getPlayerAttendanceStats(sessions[0].id, squad);
            return {
              squad,
              stats: {
                totalSessions: sessions.length,
                totalHours: sessions.reduce((sum, s) => sum + s.duration, 0),
                presentSessions: stats.filter(s => s.present).length,
                presentHours: stats.filter(s => s.present).length * sessions[0].duration,
                players: stats.map(s => ({
                  id: s.player_id,
                  name: s.players.name,
                  present: s.present ? 1 : 0,
                  absent: s.present ? 0 : 1,
                  totalHours: s.present ? sessions[0].duration : 0
                }))
              }
            };
          })
        );

        generatePlayerAttendanceReport({
          sessions,
          playerStats,
          startDate,
          endDate,
          selectedSquads,
          separateReports
        });

        toast.success('Player attendance report generated successfully');
        onClose();
      }
    } catch (error) {
      console.error('Error generating report:', error);
      toast.error('Failed to generate report. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-auto">
        <div className="px-6 py-4 border-b flex justify-between items-center">
          <h3 className="text-lg font-medium text-gray-900 flex items-center">
            <Calendar size={20} className="mr-2 text-navy-800" />
            Attendance Report
          </h3>
          <button
            type="button"
            className="text-gray-400 hover:text-gray-500"
            onClick={onClose}
          >
            <span className="sr-only">Close</span>
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        
        <div className="px-6 py-4 max-h-[70vh] overflow-y-auto">
          <div className="space-y-6">
            <div className="form-group">
              <label className="form-label">Report Type</label>
              <div className="mt-2 grid grid-cols-2 gap-2">
                <button
                  type="button"
                  className={`px-4 py-2 rounded-md flex items-center justify-center ${
                    reportSubject === 'player'
                      ? 'bg-navy-800 text-white'
                      : 'bg-white text-navy-800 border border-navy-800'
                  }`}
                  onClick={() => setReportSubject('player')}
                >
                  <Users size={20} className="mr-2" />
                  Player Attendance
                </button>
                <button
                  type="button"
                  className={`px-4 py-2 rounded-md flex items-center justify-center ${
                    reportSubject === 'coach'
                      ? 'bg-navy-800 text-white'
                      : 'bg-white text-navy-800 border border-navy-800'
                  }`}
                  onClick={() => setReportSubject('coach')}
                >
                  <User size={20} className="mr-2" />
                  Coach Attendance
                </button>
              </div>
            </div>
            
            <div className="form-group">
              <label className="form-label">Date Range</label>
              <div className="mt-2 grid grid-cols-3 gap-2">
                <button
                  type="button"
                  className={`px-4 py-2 rounded-md ${
                    dateRange === 'week'
                      ? 'bg-navy-800 text-white'
                      : 'bg-white text-navy-800 border border-navy-800'
                  }`}
                  onClick={() => handleDateRangeChange('week')}
                >
                  This Week
                </button>
                <button
                  type="button"
                  className={`px-4 py-2 rounded-md ${
                    dateRange === 'month'
                      ? 'bg-navy-800 text-white'
                      : 'bg-white text-navy-800 border border-navy-800'
                  }`}
                  onClick={() => handleDateRangeChange('month')}
                >
                  This Month
                </button>
                <button
                  type="button"
                  className={`px-4 py-2 rounded-md ${
                    dateRange === 'custom'
                      ? 'bg-navy-800 text-white'
                      : 'bg-white text-navy-800 border border-navy-800'
                  }`}
                  onClick={() => setDateRange('custom')}
                >
                  Custom
                </button>
              </div>
            </div>
            
            {dateRange === 'custom' && (
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="form-group">
                  <label htmlFor="startDate" className="form-label">Start Date</label>
                  <input
                    type="date"
                    id="startDate"
                    className="form-input"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="endDate" className="form-label">End Date</label>
                  <input
                    type="date"
                    id="endDate"
                    className="form-input"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                  />
                </div>
              </div>
            )}
            
            {reportSubject === 'coach' ? (
              <div className="form-group">
                <label htmlFor="coachSelect" className="form-label flex items-center">
                  <User size={16} className="mr-2" />
                  Select Coach (Optional)
                </label>
                <p className="text-sm text-gray-500 mb-2">
                  Leave blank to generate a report for all coaches
                </p>
                <select
                  id="coachSelect"
                  className="form-input"
                  value={selectedCoach}
                  onChange={(e) => setSelectedCoach(e.target.value)}
                >
                  <option value="">All Coaches</option>
                  {coaches.map(coach => (
                    <option key={coach.id} value={coach.id}>
                      {coach.name}
                    </option>
                  ))}
                </select>
              </div>
            ) : (
              <>
                <div className="form-group">
                  <label className="form-label flex items-center">
                    <Filter size={16} className="mr-2" />
                    Select Squads for Reports
                  </label>
                  <p className="text-sm text-gray-500 mb-2">
                    Select the squads you want to generate attendance reports for
                  </p>
                  <div className="mt-2 grid grid-cols-2 sm:grid-cols-4 gap-2">
                    {(['U11', 'U13', 'U15', 'U17'] as Squad[]).map(squad => (
                      <div key={squad} className="flex items-center">
                        <input
                          id={`squad-${squad}`}
                          type="checkbox"
                          checked={selectedSquads.includes(squad)}
                          onChange={(e) => handleSquadChange(squad, e.target.checked)}
                          className="h-5 w-5 text-navy-600 focus:ring-navy-500 border-gray-300 rounded"
                        />
                        <label htmlFor={`squad-${squad}`} className="ml-2 block text-sm text-gray-900">
                          {squad}
                        </label>
                      </div>
                    ))}
                  </div>
                </div>
                
                <div className="form-group">
                  <label className="form-label flex items-center">
                    <FileText size={16} className="mr-2" />
                    Report Format
                  </label>
                  <div className="mt-2 space-y-2">
                    <div className="flex items-center">
                      <input
                        id="combined-file"
                        type="radio"
                        checked={!separateReports}
                        onChange={() => setSeparateReports(false)}
                        className="h-5 w-5 text-navy-600 focus:ring-navy-500 border-gray-300"
                      />
                      <label htmlFor="combined-file" className="ml-2 block text-sm text-gray-900">
                        Generate one combined file for all squads
                      </label>
                    </div>
                    <div className="flex items-center">
                      <input
                        id="separate-files"
                        type="radio"
                        checked={separateReports}
                        onChange={() => setSeparateReports(true)}
                        className="h-5 w-5 text-navy-600 focus:ring-navy-500 border-gray-300"
                      />
                      <label htmlFor="separate-files" className="ml-2 block text-sm text-gray-900">
                        Generate separate file for each squad
                      </label>
                    </div>
                  </div>
                </div>
              </>
            )}
            
            <div className="bg-gray-50 p-4 rounded-md">
              <h4 className="text-sm font-medium text-gray-700 mb-2">Report Preview</h4>
              <div className="text-sm text-gray-600">
                <p><span className="font-medium">Report Type:</span> {reportSubject === 'player' ? 'Player Attendance' : 'Coach Attendance'}</p>
                <p><span className="font-medium">Date Range:</span> {format(new Date(startDate), 'MMM d, yyyy')} - {format(new Date(endDate), 'MMM d, yyyy')}</p>
                {reportSubject === 'player' && (
                  <>
                    <p><span className="font-medium">Squads:</span> {selectedSquads.join(', ')}</p>
                    <p><span className="font-medium">Format:</span> {separateReports ? 'Separate files per squad' : 'One combined file'}</p>
                  </>
                )}
                {reportSubject === 'coach' && (
                  <p><span className="font-medium">Coach:</span> {selectedCoach ? coaches.find(c => c.id === selectedCoach)?.name : 'All Coaches'}</p>
                )}
              </div>
            </div>
          </div>
        </div>
        
        <div className="px-6 py-4 bg-gray-50 flex justify-end rounded-b-lg">
          <button
            type="button"
            className="btn-outline mr-2"
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn-primary flex items-center"
            onClick={handleGeneratePDF}
            disabled={isGenerating || (reportSubject === 'player' && selectedSquads.length === 0)}
          >
            <Download size={20} className="mr-2" />
            {isGenerating ? 'Generating...' : 'Download Report'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AttendanceReportModal;